/**
 * v101.98.0 · FloatingPanel
 * Panel desplegable (menú ⋮, lista de opciones, autocompletar) que se dibuja en <body> con posición fija junto a su
 * ancla. Así NUNCA queda cortado por una tabla, una tarjeta o un modal con overflow, y siempre queda por encima
 * (capa 10100, ver index.css · .afc-floating-menu).
 *  - Se abre hacia abajo; si no cabe, hacia arriba. La altura se limita al espacio libre (hace scroll).
 *  - Sigue al ancla al hacer scroll (en cualquier contenedor), al cambiar el tamaño de la ventana o el ZOOM.
 *  - El panel lleva data-afc-floating: los «clic afuera» de cada componente deben ignorarlo
 *    (helper isInsideFloating).
 *
 * Uso:
 *   const anchorRef = useRef(null);
 *   <div ref={anchorRef} className="relative">… botón …</div>
 *   {open && <FloatingPanel anchorRef={anchorRef} align="end" className="bg-white border rounded-lg shadow-lg">…</FloatingPanel>}
 */
import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export function isInsideFloating(target) {
  try { return !!(target && target.closest && target.closest("[data-afc-floating]")); } catch { return false; }
}

const FloatingPanel = forwardRef(function FloatingPanel(
  { anchorRef, align = "start", matchWidth = false, offset = 4, minWidth, maxHeight = 420, className = "", style = {},
    children, ...rest },
  ref,
) {
  const innerRef = useRef(null);
  const [pos, setPos] = useState({ top: -9999, left: -9999, maxH: maxHeight, width: undefined });

  const setRefs = useCallback((el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);

  const place = useCallback(() => {
    const a = anchorRef?.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const el = innerRef.current;
    const w = matchWidth ? r.width : (el ? el.offsetWidth : 0);
    const natH = el ? el.scrollHeight : 0;
    const below = vh - r.bottom - offset - 8;
    const above = r.top - offset - 8;
    const openUp = natH > below && above > below;
    const maxH = Math.max(120, Math.min(maxHeight, openUp ? above : below));
    let left = align === "end" ? r.right - w : r.left;
    left = Math.max(8, Math.min(left, vw - w - 8));
    const h = Math.min(natH || maxH, maxH);
    const top = openUp ? Math.max(8, r.top - offset - h) : r.bottom + offset;
    setPos({ top, left, maxH, width: matchWidth ? r.width : undefined });
  }, [anchorRef, align, matchWidth, offset, maxHeight]);

  useLayoutEffect(() => {
    place();
    const raf = requestAnimationFrame(place);
    const onMove = () => place();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [place]);

  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={setRefs}
      data-afc-floating=""
      className={`afc-floating-menu ${className}`}
      style={{
        position: "fixed", top: pos.top, left: pos.left, maxHeight: pos.maxH, overflowY: "auto",
        width: pos.width, minWidth: minWidth, maxWidth: "calc(100vw - 16px)", ...style,
      }}
      {...rest}
    >
      {children}
    </div>,
    document.body,
  );
});

export default FloatingPanel;
